import { useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import { useRBAC } from '../contexts/RBACContext';

/**
 * Hook that wraps DataContext data with RBAC filtering
 * This provides role-based filtered access to leads and events
 * without modifying the original DataContext
 */
export function useRBACData() {
  const dataContext = useData();
  const { filterByCampaign, userRole, assignedCampaigns, isAdminOrHigher } = useRBAC();

  // Filter leads by assigned campaigns
  const filteredLeads = useMemo(() => {
    if (!dataContext.leads) return [];
    return filterByCampaign(dataContext.leads, 'campaign_name');
  }, [dataContext.leads, filterByCampaign]);

  // Filter lead events by assigned campaigns
  const filteredLeadEvents = useMemo(() => {
    if (!dataContext.leadEvents) return [];
    return filterByCampaign(dataContext.leadEvents, 'campaign_name');
  }, [dataContext.leadEvents, filterByCampaign]);

  // Filter messages - match by lead email that belongs to assigned campaigns
  const filteredMessages = useMemo(() => {
    if (!dataContext.messages) return [];
    if (isAdminOrHigher()) return dataContext.messages;
    
    // Get emails of leads in assigned campaigns
    const allowedEmails = new Set(filteredLeads.map(lead => lead.email));
    return dataContext.messages.filter(msg => allowedEmails.has(msg.lead_email));
  }, [dataContext.messages, filteredLeads, isAdminOrHigher]);

  // Get filtered dashboard stats
  const getFilteredDashboardStats = useMemo(() => {
    return () => {
      const leads = filteredLeads;
      const events = filteredLeadEvents;
      
      const totalLeads = leads.length;
      const callListLeads = leads.filter(lead => {
        if (lead.tags && lead.tags.includes('removed_from_call_list')) return false;
        if (lead.status === 'call_list') return true;
        if (lead.status === 'interested' && lead.last_reply) return true;
        if ((lead.score || 0) >= 20) return true;
        return false;
      }).length;
      const avgScore = leads.length > 0 
        ? Math.round(leads.reduce((sum, lead) => sum + (lead.score || 0), 0) / leads.length)
        : 0;
      
      const uniqueOpens = new Set(events.filter(e => e.event_type === 'open').map(e => e.lead_email)).size;
      const uniqueClicks = new Set(events.filter(e => e.event_type === 'click').map(e => e.lead_email)).size;
      // Only count replies if both event_type is 'reply' AND reply_text exists (two-condition rule)
      const uniqueReplies = new Set(events.filter(e => e.event_type === 'reply' && e.reply_text && e.reply_text.trim().length > 0).map(e => e.lead_email)).size;
      const totalSent = events.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent').length;
      const campaigns = [...new Set(leads.map(lead => lead.campaign_name).filter(Boolean))].length;

      return {
        totalLeads,
        callListLeads,
        avgScore,
        opens: uniqueOpens,
        clicks: uniqueClicks,
        replies: uniqueReplies,
        sent: totalSent,
        campaigns
      };
    };
  }, [filteredLeads, filteredLeadEvents]);

  // Check if user can access specific campaign filter
  const canFilterByCampaign = (campaignName) => {
    if (isAdminOrHigher()) return true;
    if (!campaignName) return true;
    if (!assignedCampaigns || assignedCampaigns.length === 0) return false;
    return assignedCampaigns.includes(campaignName);
  };

  // Get allowed campaigns for filter dropdown
  const getAllowedCampaigns = useMemo(() => {
    if (isAdminOrHigher()) {
      // Admin/Developer sees all campaigns
      return [...new Set(dataContext.leads?.map(l => l.campaign_name).filter(Boolean) || [])];
    }
    // Others only see assigned campaigns
    return assignedCampaigns || [];
  }, [dataContext.leads, assignedCampaigns, isAdminOrHigher]);

  return {
    // Original context (for admin/developer who need full access)
    ...dataContext,
    
    // Filtered data (respects RBAC)
    leads: filteredLeads,
    leadEvents: filteredLeadEvents,
    messages: filteredMessages,
    
    // RBAC-aware stats
    getDashboardStats: getFilteredDashboardStats,
    
    // Helper functions
    canFilterByCampaign,
    getAllowedCampaigns,
    
    // Original unfiltered data (for specific admin needs)
    allLeads: dataContext.leads,
    allLeadEvents: dataContext.leadEvents,
    allMessages: dataContext.messages,
    
    // Role info
    userRole,
    assignedCampaigns,
    isAdminOrHigher
  };
}

export default useRBACData;
